// Register.tsx
import React, { useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Check } from 'lucide-react';
import './Register.scss';
import { RegisterContent } from '../../../types/RegisterContent';

/* ---------- Product content (unchanged) ---------- */
type ProductKey =
  | 'swiftgrid'
  | 'crm'
  | 'accounting'
  | 'email-calendars'
  | 'hospitality'
  | 'academic'
  | 'default';

const REGISTER_CONTENT: Record<ProductKey, RegisterContent> = {
  swiftgrid: {
    brandName: 'Cilpron',
    brandAccent: 'SwiftGrid',
    title: 'Start sending reliable email in minutes',
    description:
      'Join thousands of teams using SwiftGrid for transactional and marketing email without running their own infrastructure.',
    benefits: [
      'Free tier included — no credit card required',
      'Send your first email in under 5 minutes',
      'API + SMTP relay in every plan',
      'Domain authentication & deliverability tools',
    ],
  },
  crm: {
    brandName: 'Cilpron',
    brandAccent: 'CRM',
    title: 'Grow relationships, not spreadsheets',
    description:
      'Join teams that use Cilpron CRM to manage pipelines, automate follow-ups, and close more deals with less effort.',
    benefits: [
      'Free tier to get started',
      'Pipeline & contact management',
      'Automated workflows & reminders',
      'Insights that help you close faster',
    ],
  },
  accounting: {
    brandName: 'Cilpron',
    brandAccent: 'Accounting',
    title: 'Modern accounting for growing teams',
    description:
      'Invoices, expenses, and reports in one place — built for businesses that want clarity without complexity.',
    benefits: [
      'Free tier included',
      'Invoicing & expense tracking',
      'Real-time financial overview',
      'Export-ready reports',
    ],
  },
  'email-calendars': {
    brandName: 'Cilpron',
    brandAccent: 'Email & Calendars',
    title: 'Email and calendars that work together',
    description:
      'Unified inbox, shared calendars, and smart scheduling — designed for teams that need to stay in sync.',
    benefits: [
      'Free tier included',
      'Shared inboxes & calendars',
      'Smart scheduling tools',
      'Works with your existing workflow',
    ],
  },
  hospitality: {
    brandName: 'Cilpron',
    brandAccent: 'Hospitality',
    title: 'Hospitality software that guests notice',
    description:
      'Reservations, guest communication, and operations in one platform built for hotels and hospitality teams.',
    benefits: [
      'Free tier to get started',
      'Reservation & guest management',
      'Automated guest messaging',
      'Operations tools that scale',
    ],
  },
  academic: {
    brandName: 'Cilpron',
    brandAccent: 'Academic',
    title: 'Academic portals made simple',
    description:
      'Student records, course management, and communication tools designed for schools and academic institutions.',
    benefits: [
      'Free tier included',
      'Student & course management',
      'Parent & staff communication',
      'Secure and easy to administer',
    ],
  },
  default: {
    brandName: 'Cilpron',
    brandAccent: '',
    title: 'Create your free account',
    description: 'Start using Cilpron products with a free tier. No credit card required.',
    benefits: [
      'Free tier included',
      'No credit card required',
      'Access to core features',
      'Upgrade anytime',
    ],
  },
};

/* ---------- Static option data ---------- */
const DIAL_CODES = [
  { code: 'NG', dial: '+234', flag: '🇳🇬', label: 'Nigeria' },
  { code: 'GH', dial: '+233', flag: '🇬🇭', label: 'Ghana' },
  { code: 'KE', dial: '+254', flag: '🇰🇪', label: 'Kenya' },
  { code: 'ZA', dial: '+27', flag: '🇿🇦', label: 'South Africa' },
  { code: 'US', dial: '+1', flag: '🇺🇸', label: 'United States' },
  { code: 'GB', dial: '+44', flag: '🇬🇧', label: 'United Kingdom' },
  { code: 'CA', dial: '+1', flag: '🇨🇦', label: 'Canada' },
];

const COUNTRIES = [
  'Nigeria',
  'Ghana',
  'Kenya',
  'South Africa',
  'United States',
  'United Kingdom',
  'Canada',
  'Other',
];

const INDUSTRIES = [
  'Technology',
  'Finance',
  'Healthcare',
  'Education',
  'Hospitality',
  'Retail',
  'Manufacturing',
  'Other',
];

const JOB_TITLES = [
  'CEO / Founder',
  'CTO / VP Engineering',
  'Head of Sales',
  'Marketing Manager',
  'Operations',
  'Other',
];

const COMPANY_SIZES = ['1–10', '11–50', '51–200', '201–500', '501–1,000', '1,000+'];

interface FormState {
  firstName: string;
  lastName: string;
  email: string;
  country: string;
  dialCode: string;
  phone: string;
  company: string;
  industry: string;
  jobTitle: string;
  favoriteProducts: string;
  budget: string;
  companySize: string;
  marketingOptIn: 'yes' | 'no' | '';
  agreeTerms: boolean;
}

const INITIAL_FORM: FormState = {
  firstName: '',
  lastName: '',
  email: '',
  country: '',
  dialCode: '+234',
  phone: '',
  company: '',
  industry: '',
  jobTitle: '',
  favoriteProducts: '',
  budget: '',
  companySize: '',
  marketingOptIn: '',
  agreeTerms: false,
};

const Register: React.FC = () => {
  const location = useLocation();
  const product = ((location.state as { product?: ProductKey })?.product) || 'default';
  const content = REGISTER_CONTENT[product] ?? REGISTER_CONTENT.default;

  const [form, setForm] = useState<FormState>(INITIAL_FORM);
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(false);

  const emailValid = useMemo(
    () => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email),
    [form.email],
  );

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    setForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const markTouched = (names: string[]) =>
    setTouched((prev) => ({
      ...prev,
      ...Object.fromEntries(names.map((n) => [n, true])),
    }));

  const err = (field: keyof FormState, condition: boolean) =>
    touched[field] && !condition;

  const isFormValid = () =>
    form.firstName.trim() &&
    form.lastName.trim() &&
    emailValid &&
    form.country &&
    form.phone.trim().length >= 6 &&
    form.company.trim() &&
    form.industry &&
    form.agreeTerms &&
    form.marketingOptIn !== '';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    markTouched([
      'firstName',
      'lastName',
      'email',
      'country',
      'phone',
      'company',
      'industry',
      'agreeTerms',
      'marketingOptIn',
    ]);
    if (!isFormValid()) return;

    setLoading(true);
    try {
      // await registerUser(form);
      console.log('Register payload:', form);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="cp-register-page">
     
      {/* Hero */}
      <header className="cp-hero-signup">
        <div className="cp-hero__overlay" />
        <div className="cp-hero__content">
          <h1>GET PRICING / INFO</h1>
          <p>
            Please fill in the form below.
            <br />
            Our sales representative will contact you as soon as possible.
          </p>
        </div>
      </header>

      {/* Form card */}
      <main className="cp-form-wrapper">
        <form className="cp-form-card" onSubmit={handleSubmit} noValidate>
          <p className="cp-mandatory-note">* Mandatory</p>

          {/* Row 1 */}
          <div className="cp-grid">
            <div className={`cp-field ${err('firstName', !!form.firstName.trim()) ? 'has-error' : ''}`}>
              <label htmlFor="firstName">
                First Name <span className="req">*</span>
              </label>
              <input
                id="firstName"
                name="firstName"
                type="text"
                value={form.firstName}
                onChange={handleChange}
                onBlur={() => markTouched(['firstName'])}
                autoComplete="given-name"
              />
            </div>

            <div className={`cp-field ${err('lastName', !!form.lastName.trim()) ? 'has-error' : ''}`}>
              <label htmlFor="lastName">
                Last Name <span className="req">*</span>
              </label>
              <input
                id="lastName"
                name="lastName"
                type="text"
                value={form.lastName}
                onChange={handleChange}
                onBlur={() => markTouched(['lastName'])}
                autoComplete="family-name"
              />
            </div>
          </div>

          {/* Row 2 */}
          <div className="cp-grid">
            <div className={`cp-field ${err('email', emailValid) ? 'has-error' : ''}`}>
              <label htmlFor="email">
                Email <span className="req">*</span>
              </label>
              <input
                id="email"
                name="email"
                type="email"
                value={form.email}
                onChange={handleChange}
                onBlur={() => markTouched(['email'])}
                autoComplete="email"
              />
            </div>

            <div className={`cp-field ${err('country', !!form.country) ? 'has-error' : ''}`}>
              <label htmlFor="country">
                Country/Region <span className="req">*</span>
              </label>
              <select
                id="country"
                name="country"
                value={form.country}
                onChange={handleChange}
                onBlur={() => markTouched(['country'])}
              >
                <option value="" disabled>
                  Select country
                </option>
                {COUNTRIES.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Row 3 */}
          <div className="cp-grid">
            <div className={`cp-field ${err('phone', form.phone.trim().length >= 6) ? 'has-error' : ''}`}>
              <label htmlFor="phone">
                Telephone <span className="req">*</span>
              </label>
              <div className="cp-phone">
                <select
                  name="dialCode"
                  value={form.dialCode}
                  onChange={handleChange}
                  aria-label="Country calling code"
                >
                  {DIAL_CODES.map((d) => (
                    <option key={d.code + d.dial} value={d.dial}>
                      {d.flag} {d.dial}
                    </option>
                  ))}
                </select>
                <input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={form.phone}
                  onChange={handleChange}
                  onBlur={() => markTouched(['phone'])}
                  autoComplete="tel-national"
                  placeholder=" "
                />
              </div>
            </div>

            <div className={`cp-field ${err('company', !!form.company.trim()) ? 'has-error' : ''}`}>
              <label htmlFor="company">
                Company <span className="req">*</span>
              </label>
              <input
                id="company"
                name="company"
                type="text"
                value={form.company}
                onChange={handleChange}
                onBlur={() => markTouched(['company'])}
                autoComplete="organization"
              />
            </div>
          </div>

          {/* Row 4 */}
          <div className="cp-grid">
            <div className={`cp-field ${err('industry', !!form.industry) ? 'has-error' : ''}`}>
              <label htmlFor="industry">
                Industry <span className="req">*</span>
              </label>
              <select
                id="industry"
                name="industry"
                value={form.industry}
                onChange={handleChange}
                onBlur={() => markTouched(['industry'])}
              >
                <option value="" disabled>
                  Select industry
                </option>
                {INDUSTRIES.map((i) => (
                  <option key={i} value={i}>
                    {i}
                  </option>
                ))}
              </select>
            </div>

            <div className="cp-field">
              <label htmlFor="jobTitle">Job Title</label>
              <select
                id="jobTitle"
                name="jobTitle"
                value={form.jobTitle}
                onChange={handleChange}
              >
                <option value="" disabled>
                  Select job title
                </option>
                {JOB_TITLES.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Row 5 */}
          <div className="cp-grid">
            <div className="cp-field">
              <label htmlFor="favoriteProducts">Favorite Products</label>
              <select
                id="favoriteProducts"
                name="favoriteProducts"
                value={form.favoriteProducts}
                onChange={handleChange}
              >
                <option value="" disabled>
                  Select product
                </option>
                <option value="swiftgrid">SwiftGrid</option>
                <option value="crm">CRM</option>
                <option value="accounting">Accounting</option>
                <option value="email-calendars">Email & Calendars</option>
                <option value="hospitality">Hospitality</option>
                <option value="academic">Academic</option>
              </select>
            </div>

            <div className="cp-field">
              <label htmlFor="budget">Budget</label>
              <select
                id="budget"
                name="budget"
                value={form.budget}
                onChange={handleChange}
              >
                <option value="" disabled>
                  Select budget
                </option>
                <option value="<5k">&lt; $5,000</option>
                <option value="5-20k">$5,000 – $20,000</option>
                <option value="20-50k">$20,000 – $50,000</option>
                <option value="50k+">$50,000+</option>
              </select>
            </div>
          </div>

          {/* Dark note bar */}
          <div className="cp-note-bar">
            * Briefly outline your project requirements. (For information about Sass, Enterprise software, Agentic AI, or home Internet products, please visit Cilpron’s consumer website.)
          </div>

          {/* Marketing radio */}
          <div
            className={`cp-consent-block ${
              err('marketingOptIn', form.marketingOptIn !== '') ? 'has-error' : ''
            }`}
          >
            <p>
              * I agree to receive information about products, solutions, services, and
              offerings from Cilpron and Cilpron authorized partners. I understand that I
              can unsubscribe at any time
            </p>
            <div className="cp-radios">
              <label className="cp-radio">
                <input
                  type="radio"
                  name="marketingOptIn"
                  value="yes"
                  checked={form.marketingOptIn === 'yes'}
                  onChange={handleChange}
                  onBlur={() => markTouched(['marketingOptIn'])}
                />
                <span>Yes</span>
              </label>
              <label className="cp-radio">
                <input
                  type="radio"
                  name="marketingOptIn"
                  value="no"
                  checked={form.marketingOptIn === 'no'}
                  onChange={handleChange}
                  onBlur={() => markTouched(['marketingOptIn'])}
                />
                <span>No</span>
              </label>
            </div>
          </div>

          {/* Privacy checkbox */}
          <div
            className={`cp-consent-block ${
              err('agreeTerms', form.agreeTerms) ? 'has-error' : ''
            }`}
          >
            <label className="cp-checkbox">
              <input
                type="checkbox"
                name="agreeTerms"
                checked={form.agreeTerms}
                onChange={handleChange}
                onBlur={() => markTouched(['agreeTerms'])}
              />
              <span className="cp-checkbox__box">
                <Check size={12} strokeWidth={3} />
              </span>
              <span>
                I understand and agree to the use and transfer of my personal information by
                Cilpron according to Cilpron’s{' '}
                <a href="/privacy" target="_blank" rel="noreferrer">
                  Privacy Policy
                </a>{' '}
                and{' '}
                <a href="/terms" target="_blank" rel="noreferrer">
                  Terms of Use
                </a>
                .
              </span>
            </label>
          </div>

          <button type="submit" className="cp-submit" disabled={loading}>
            {loading ? 'Submitting…' : 'Submit'}
          </button>
        </form>
      </main>

      {/* Floating phone button */}
      <a href="tel:+2348012345678" className="cp-fab-phone" aria-label="Call us">
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
        </svg>
      </a>
    </div>
  );
};

export default Register;